import { useState } from 'react';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';

import AiChatPanel from '../../components/AiChatPanel/AiChatPanel';
import DockRightSlotProvider from '../../components/DraggablePanel/context/DockRightSlotContext';
import DockRightSlot from '../../components/DraggablePanel/DockRightSlot';
import BackdropInsets from '../../components/Modal/BackdropInsets';
import SelectDefaultLanguageModal from '../../components/SelectDefaultLanguageModal/SelectDefaultLanguageModal';
import SidePanelSlotProvider from '../../components/SidePanel/context/SidePanelSlotContext';
import SidePanelSlot from '../../components/SidePanel/SidePanelSlot';
import { AppRoutes } from '../../constants/AppRoutes';
import AiChatPanelProvider from '../../contexts/AiChatPanelContext';
import AiScreenActionsProvider from '../../contexts/AiScreenActionsContext';
import AiScreenContextProvider from '../../contexts/AiScreenContext';
import useValueChainProcessingSync from '../../hooks/useValueChainProcessingSync';
import { useAppSelector } from '../../store/hooks/useApp';
import selectFullScreenActive from '../../store/slices/general/selectors';
import Header from '../Header/Header';
import Sidebar from '../Sidebar/Sidebar';

import type { ReactElement } from 'react';
import type IPage from './interfaces/IPage';

import styles from './sass/Page.module.scss';

/**
 * The page frame. Mounts the AI chat panel and the screen context registry once, over both
 * the full-screen branch and the normal one.
 *
 * @see [3. Components](../../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
function Page({ isLanguageSelectorModalOpen = false }: IPage): ReactElement {
  const location = useLocation();
  const { pathname } = location;

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [main, setMain] = useState<HTMLDivElement | null>(null);

  const noPadding = !!matchPath({ path: AppRoutes.companyMap }, pathname);
  const isFullScreenActive = useAppSelector(selectFullScreenActive);
  useValueChainProcessingSync();

  const content = isFullScreenActive ? (
    <div className={`page ${styles.root}`}>
      <div className={styles.content}>
        <div
          ref={setMain}
          className={clsx(styles.main, {
            [styles.noPadding]: noPadding,
          })}
        >
          <Outlet context={{ setIsSidebarOpen }} />
        </div>
        <SidePanelSlot />
        <DockRightSlot />
      </div>
      <SelectDefaultLanguageModal open={isLanguageSelectorModalOpen} />
    </div>
  ) : (
    <div className={`page ${styles.root}`}>
      <Header />
      <div className={styles.content}>
        {isSidebarOpen ? <Sidebar /> : null}
        <div
          ref={setMain}
          className={clsx(styles.main, {
            [styles.noPadding]: noPadding,
          })}
        >
          <Outlet context={{ setIsSidebarOpen }} />
        </div>
        <SidePanelSlot />
        <DockRightSlot />
      </div>
      <SelectDefaultLanguageModal open={isLanguageSelectorModalOpen} />
    </div>
  );

  // The panel sits outside the two branches, so entering full screen neither unmounts the chat
  // nor drops what the screens under it have declared.
  return (
    <AiChatPanelProvider>
      <AiScreenContextProvider>
        <AiScreenActionsProvider>
          <SidePanelSlotProvider>
            <DockRightSlotProvider>
              {content}
              <BackdropInsets main={main} />
              <AiChatPanel />
            </DockRightSlotProvider>
          </SidePanelSlotProvider>
        </AiScreenActionsProvider>
      </AiScreenContextProvider>
    </AiChatPanelProvider>
  );
}

export default Page;
